import { Injectable } from '@angular/core';
import { catchError, filter, map, Observable, of, switchMap, take, throwError } from 'rxjs';
import { KeycloakService } from './keycloak.service';
import { AuthUtils } from 'src/app/shared/util/auth.utils';
import { AuthForm } from '../model/auth.model';
import { UserDataService } from '../../service/user-data.service';
import { environment } from 'src/environments/environment';
import { ValidatorsUtils } from 'src/app/shared/util/validator.utils';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly ACCESS_TOKEN = 'access_token';

  constructor(
    private _keycloakService: KeycloakService,
    private _userDataService: UserDataService,
  ) {}

  get accessToken(): string {
    return sessionStorage.getItem(this.ACCESS_TOKEN);
  }

  public removeToken(): void {
    sessionStorage.removeItem(this.ACCESS_TOKEN);
  }

  public login(authForm: AuthForm): Observable<boolean> {
    return this._keycloakService.auth(authForm).pipe(
      switchMap((response: any) => {
        if (response === false || !response.accessToken) {
          console.warn("Falha na autenticação: resposta inválida do auth.");
          return of(false);
        }

        if (!AuthUtils.hasSerasaJudScope(response.accessToken)) {
          console.warn("Acesso restrito - scope inválido.");
          return throwError(
            () =>
              new Error(
                "Acesso restrito para Servidores do Poder Judiciário.\n Para acessar o SerasaJud, é obrigatório realizar o login apenas utilizando CPF e senha cadastrados no sistema Corporativo do CNJ.",
              ),
          );
        }
        return this.saveSessionData(response.accessToken);
      }),
    );
  }

  public saveSessionData(token: string): Observable<boolean> {
    sessionStorage.setItem(this.ACCESS_TOKEN, token);
    return this._userDataService.salvarUserData(token).pipe(map(() => true));
  }

  logout(loginAgain: boolean): void {
    this.removeToken();
    this._userDataService.userData.next(null);
    if (loginAgain) {
      window.location.href = `${environment.auth.ssoLogout}?post_logout_redirect_uri=${environment.homeSerasajud}/login`;
    } else {
      window.location.href = `${environment.auth.ssoLogout}?post_logout_redirect_uri=${environment.siteserasa}`;
    }
  }

  get isAuthenticated(): boolean {
    const access_token = this.accessToken;
    if (!access_token) {
      console.warn("Falha na autenticação: access token não encontrado.");
      return false;
    }
    var userData = this._userDataService.userData.getValue();

    if (!AuthUtils.hasSerasaJudScope(access_token)) {
      console.warn("Falha na autenticação: token sem scope SerasaJud.");
      return false;
    }

    if (AuthUtils.isTokenExpired(access_token)) {
      console.warn("Falha na autenticação: token expirado.");
      return false;
    }

    if (userData.orgaoExterno != null && userData.orgaoExterno != 0) {
      console.warn(
        "Falha na autenticação: usuário com órgão externo inválido.",
      );
      return false;
    }

    if (!ValidatorsUtils.isValidaEmail(userData.email)) {
      console.warn("Falha na autenticação: e-mail do usuário inválido.");
      return false;
    }

    return true;
  }

  public isAuthenticatedAsync(): Observable<boolean> {
    return this._userDataService.userData.pipe(

      take(1),
      map(() => this.isAuthenticated),
      catchError(() => of(false)),
    );
  }
}
